import { Alert, Button, InputNumber, Space } from "antd"
import React, { useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { fetchSetupStatus, selectSetupStatus, updateSetupTargetThunk } from "../redux/branches.slice.ts"

/**
 * A resumable reminder for an owner who planned multiple branches at
 * signup (or later, on the company's own Django Admin page) but hasn't
 * activated them all yet. Never shown once `setupStatus.done` — a
 * single-operation account (target === 1) is never "incomplete" by this
 * measure, and reaching the planned count clears the reminder on its own.
 * "Continue setup" opens the full `BranchSetupWizard` rather than a bare
 * add-branch dialog, since it also covers configuring the original branch.
 * "Revise the plan" writes straight through `updateSetupTargetThunk` —
 * the planned count is onboarding intent, not a limit, so lowering it to
 * match what's actually been built is always a valid, one-step action.
 */
export function BranchSetupReminder({ onContinueSetup }: { onContinueSetup: () => void }) {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectSetupStatus)
  const [revising, setRevising] = useState(false)
  const [draftTarget, setDraftTarget] = useState<number | null>(null)

  React.useEffect(() => {
    dispatch(fetchSetupStatus())
  }, [dispatch])

  if (!status || status.done || status.target <= 1) {
    return null
  }

  const remaining = status.target - status.created

  return (
    <Alert
      type="info"
      showIcon
      className="mb-4"
      message={`Branch setup in progress — ${status.created} of ${status.target} planned branches active`}
      description={
        <Space direction="vertical" size="small">
          <span>
            You planned {status.target} branches and have {status.created} active
            {remaining > 0 ? ` (${remaining} more to go)` : ""}. You can add the rest
            whenever you&apos;re ready, or revise the plan below.
          </span>
          {revising ? (
            <Space>
              <InputNumber
                min={2}
                defaultValue={status.target}
                onChange={(value) => setDraftTarget(typeof value === "number" ? value : null)}
                data-testid="revise-planned-count-input"
              />
              <Button
                size="small"
                type="primary"
                disabled={!draftTarget}
                onClick={() => {
                  if (draftTarget) {
                    void dispatch(updateSetupTargetThunk(draftTarget))
                  }
                  setRevising(false)
                }}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setRevising(false)}>
                Cancel
              </Button>
            </Space>
          ) : (
            <Space>
              <Button size="small" type="primary" onClick={onContinueSetup}>
                Continue setup
              </Button>
              <Button size="small" onClick={() => setRevising(true)}>
                Revise planned count
              </Button>
            </Space>
          )}
        </Space>
      }
    />
  )
}
